import * as React from 'react'
import { CheckCircle2, PhoneOutgoing, RefreshCw, XCircle, type LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

/**
 * "Resumo Rápido" (Story 10.6, 2026-07-30, user request) — painel compacto
 * com as 4 métricas de "Resultados da IA" mais lidas de relance, num formato
 * de lista vertical em vez de cards — pensado para ficar ao lado do funil de
 * qualificação (mesma seção do dashboard). Os 4 valores já existem em
 * `data.kpis` (mesma fonte que os `KpiCard`s da seção "Resultados da IA"
 * logo abaixo) — este painel não busca dado novo, é só uma segunda vista
 * mais densa dos mesmos números.
 */
export interface QuickSummaryPanelProps {
  meetingsConfirmedByAi: number
  reschedulesTotal: number
  cancellationsCaughtByAi: number
  customersContactedByAi: number
}

interface Row {
  key: string
  label: string
  value: number
  icon: LucideIcon
  iconColor: string
}

export function QuickSummaryPanel(props: QuickSummaryPanelProps): React.ReactElement {
  const rows: Row[] = [
    {
      key: 'contacted',
      label: 'Clientes chamados pela IA',
      value: props.customersContactedByAi,
      icon: PhoneOutgoing,
      iconColor: 'text-violet-500',
    },
    {
      key: 'confirmed',
      label: 'Confirmados pela IA',
      value: props.meetingsConfirmedByAi,
      icon: CheckCircle2,
      iconColor: 'text-emerald-500',
    },
    {
      key: 'rescheduled',
      label: 'Reagendamentos',
      value: props.reschedulesTotal,
      icon: RefreshCw,
      iconColor: 'text-amber-500',
    },
    {
      key: 'cancelled',
      label: 'Cancelamentos detectados pela IA',
      value: props.cancellationsCaughtByAi,
      icon: XCircle,
      iconColor: 'text-rose-500',
    },
  ]

  return (
    <div className="space-y-3" data-testid="quick-summary-panel">
      {rows.map((row) => {
        const Icon = row.icon
        return (
          <div key={row.key} className="flex items-center gap-3" data-testid={`quick-summary-${row.key}`}>
            <span
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted',
                row.iconColor,
              )}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{row.label}</span>
            <span className="shrink-0 text-lg font-semibold tabular-nums">
              {row.value.toLocaleString('pt-BR')}
            </span>
          </div>
        )
      })}
    </div>
  )
}

export default QuickSummaryPanel
